import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getTierFeatures, getTierTokenLimit, TierFeatureFlags, FeatureKey } from "@/lib/tierFeatures";

interface SubscriptionContextType {
  tier: string | null;
  status: string | null;
  expiresAt: string | null;
  loading: boolean;
  isPro: boolean;
  isAgency: boolean;
  isActive: boolean;
  isTrial: boolean;
  isTrialExpired: boolean;
  trialRemaining: number;
  trialFirstUsedAt: string | null;
  // Freemium VK ORD specific (5 tokens, separate counter)
  trialVkTokensUsed: number;
  trialVkTokensLimit: number;
  // Shared monthly token pool (paid tiers across VK+Ozon+MediaScout)
  tokensUsed: number;
  tokensLimit: number;
  tokensRemaining: number;
  isUnlimitedTokens: boolean;
  // Per-feature access flags (matches features listed on /pricing)
  features: TierFeatureFlags;
  hasFeature: (key: FeatureKey) => boolean;
  refresh: () => Promise<void>;
  consumeTrialToken: () => Promise<{ remaining: number; ok: boolean }>;
}

const defaultFeatures = getTierFeatures(null);

const SubscriptionContext = createContext<SubscriptionContextType>({
  tier: null,
  status: null,
  expiresAt: null,
  loading: true,
  isPro: false,
  isAgency: false,
  isActive: false,
  isTrial: false,
  isTrialExpired: false,
  trialRemaining: 0,
  trialFirstUsedAt: null,
  trialVkTokensUsed: 0,
  trialVkTokensLimit: 5,
  tokensUsed: 0,
  tokensLimit: 0,
  tokensRemaining: 0,
  isUnlimitedTokens: false,
  features: defaultFeatures,
  hasFeature: () => false,
  refresh: async () => {},
  consumeTrialToken: async () => ({ remaining: 0, ok: false }),
});

export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tier, setTier] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [trialRemaining, setTrialRemaining] = useState(0);
  const [trialFirstUsedAt, setTrialFirstUsedAt] = useState<string | null>(null);
  const [trialVkTokensUsed, setTrialVkTokensUsed] = useState(0);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [tokensLimit, setTokensLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const TRIAL_VK_LIMIT = 5;

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    if (!user) {
      setTier(null);
      setStatus(null);
      setExpiresAt(null);
      setTrialRemaining(0);
      setTrialFirstUsedAt(null);
      setTrialVkTokensUsed(0);
      setTokensUsed(0);
      setTokensLimit(0);
      setLoading(false);
      return;
    }
    try {
      const { data } = await supabase
        .from("profiles")
        .select(
          "subscription_tier, subscription_status, subscription_expires_at, trial_tokens_remaining, trial_first_used_at, trial_vk_tokens_used, tokens_period_used, tokens_period_limit"
        )
        .eq("user_id", user.id)
        .single();

      if (data) {
        const d = data as any;
        setTier(d.subscription_tier);
        setStatus(d.subscription_status);
        setExpiresAt(d.subscription_expires_at);
        setTrialRemaining(d.trial_tokens_remaining ?? 0);
        setTrialFirstUsedAt(d.trial_first_used_at ?? null);
        setTrialVkTokensUsed(d.trial_vk_tokens_used ?? 0);
        setTokensUsed(d.tokens_period_used ?? 0);
        // If limit not yet set in DB (e.g. legacy profile), fall back to tier defaults
        const dbLimit = d.tokens_period_limit ?? 0;
        const fallback = getTierTokenLimit(d.subscription_tier);
        setTokensLimit(dbLimit > 0 ? dbLimit : fallback);
      }
    } catch (_e) {
      // Не блокируем весь сайт, если профиль/сеть временно недоступны.
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const consumeTrialToken = useCallback(async () => {
    const { data, error } = await supabase.rpc("consume_trial_token");
    if (error || !data || !Array.isArray(data) || data.length === 0) {
      return { remaining: trialRemaining, ok: false };
    }
    const row = data[0] as { remaining: number; ok: boolean };
    setTrialRemaining(row.remaining);
    if (row.ok && !trialFirstUsedAt) {
      setTrialFirstUsedAt(new Date().toISOString());
    }
    return row;
  }, [trialRemaining, trialFirstUsedAt]);

  const isActive =
    (status === "active" || status === "trial" || status === "grace") &&
    (!expiresAt || new Date(expiresAt) > new Date());
  const isPro = isActive && (tier === "pro" || tier === "agency" || tier?.startsWith("ent_"));
  const isAgency = isActive && (tier === "agency" || tier === "ent_agency");
  const isTrial = tier === "trial" || status === "trial";
  const isTrialExpired = isTrial && !!expiresAt && new Date(expiresAt).getTime() <= Date.now();

  // Resolve features only when subscription is active. Expired = no features.
  const features = isActive ? getTierFeatures(tier) : defaultFeatures;
  const hasFeature = useCallback((key: FeatureKey) => features[key], [features]);

  const isUnlimitedTokens = isActive && tokensLimit < 0;
  const tokensRemaining = isUnlimitedTokens ? Infinity : Math.max(0, tokensLimit - tokensUsed);

  return (
    <SubscriptionContext.Provider
      value={{
        tier,
        status,
        expiresAt,
        loading,
        isPro,
        isAgency,
        isActive,
        isTrial,
        isTrialExpired,
        trialRemaining,
        trialFirstUsedAt,
        trialVkTokensUsed,
        trialVkTokensLimit: TRIAL_VK_LIMIT,
        tokensUsed,
        tokensLimit,
        tokensRemaining,
        isUnlimitedTokens,
        features,
        hasFeature,
        refresh: fetchSubscription,
        consumeTrialToken,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}
